import React, { useState } from "react";
import GooglePlacesAutocomplete from "../components/GooglePlacesAutocomplete";
import { Helmet } from "react-helmet-async";
import PhoneInput from "react-phone-input-2";
import heroImage from "../assets/limassol.webp";
import taxi1 from "../assets/taxi-1.jpg";
import taxi2 from "../assets/taxi-2.jpg";
import taxi3 from "../assets/taxi-3.jpg";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import { motion, useReducedMotion } from "framer-motion";
import { supabase } from "../lib/supabaseClient";
import { Link } from "react-router-dom";
import {
  trackBookingClick,
  trackWhatsAppClick,
  trackPhoneClick,
  trackBookingSubmit,
} from "../utils/analytics";
import { safeTrim, sanitizeInput } from "../utils/security";
import {
  FaCalendarCheck,
  FaCar,
  FaCity,
  FaClock,
  FaMapMarkerAlt,
  FaMountain,
  FaPhoneAlt,
  FaPlane,
  FaPlaneDeparture,
  FaRoute,
  FaStar,
  FaTags,
  FaUmbrellaBeach,
  FaWater,
} from "react-icons/fa";

import "swiper/css";
import "swiper/css/pagination";
import "react-phone-input-2/lib/style.css";

const getTodayDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getCurrentTimeString = () => {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");

  return `${hours}:${minutes}`;
};

const isValidPhoneNumber = (phoneNumber) => {
  const trimmedPhone = phoneNumber.trim();
  const digitsOnly = trimmedPhone.replace(/\D/g, "");
  const hasValidCharacters = /^\+?[0-9\s().-]+$/.test(trimmedPhone);
  const isCyprusNumber = /^(?:\+357|00357)?[279]\d{7}$/.test(trimmedPhone.replace(/[\s().-]/g, ""));
  const isInternationalNumber = /^\+?[1-9]\d{7,14}$/.test(trimmedPhone.replace(/[\s().-]/g, ""));

  return hasValidCharacters && digitsOnly.length >= 8 && digitsOnly.length <= 15 && (isCyprusNumber || isInternationalNumber);
};

const validateBooking = (bookingDetails) => {
  const passengers = Number(bookingDetails.passengers);
  const luggage = Number(bookingDetails.luggage);
  const today = getTodayDateString();

  if (!bookingDetails.pickup) {
    return "Please choose your pickup location.";
  }

  if (!bookingDetails.destination) {
    return "Please choose your destination.";
  }

  if (!bookingDetails.date) {
    return "Please choose your travel date.";
  }

  if (bookingDetails.date < today) {
    return "Please choose today or a future travel date.";
  }

  if (!bookingDetails.time) {
    return "Please choose your pickup time.";
  }

  if (!Number.isInteger(passengers) || passengers < 1 || passengers > 8) {
    return "Passengers must be between 1 and 8.";
  }

  if (!Number.isInteger(luggage) || luggage < 0 || luggage > 8) {
    return "Luggage must be between 0 and 8.";
  }

  if (!bookingDetails.phone.trim()) {
    return "Please enter your phone number.";
  }

  if (!isValidPhoneNumber(bookingDetails.phone)) {
    return "Please enter a valid Cyprus or international phone number.";
  }

  return "";
};

const passengerOptions = Array.from({ length: 8 }, (_, index) => {
  const value = String(index + 1);

  return {
    value,
    label: `${value} passenger${value === "1" ? "" : "s"}`,
  };
});

const luggageOptions = Array.from({ length: 9 }, (_, index) => {
  const value = String(index);

  return {
    value,
    label: value === "0" ? "No luggage" : `${value} suitcase${value === "1" ? "" : "s"}`,
  };
});

const PremiumDropdown = ({ name, value, options, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find((option) => option.value === value) || options[0];

  const handleSelect = (nextValue) => {
    onChange({
      target: {
        name,
        value: nextValue,
      },
    });
    setIsOpen(false);
  };

  return (
    <div
      className="relative w-full"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setIsOpen(false);
        }
      }}
    >
      <button
        type="button"
        className="premium-input flex min-h-[60px] w-full items-center justify-between gap-3 rounded-2xl border border-white/10 bg-[#0b1020] px-4 py-4 text-left text-white outline-none shadow-inner shadow-black/30 ring-yellow-400/20"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      >
        <span>{selectedOption.label}</span>
        <span
          aria-hidden="true"
          className={`text-sm font-black text-yellow-400 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        >
          v
        </span>
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 z-40 mt-2 overflow-hidden rounded-2xl border border-yellow-400/25 bg-[#080d1a] p-2 shadow-2xl shadow-black/60">
          <div
            className="max-h-72 overflow-y-auto overscroll-contain"
            role="listbox"
            aria-label={name}
          >
            {options.map((option) => {
              const isSelected = option.value === value;

              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`mb-1 flex min-h-[48px] w-full items-center justify-between rounded-xl px-4 py-3 text-left text-base font-bold transition-colors last:mb-0 ${
                    isSelected
                      ? "bg-yellow-400 text-black"
                      : "text-white hover:bg-yellow-400/15 focus:bg-yellow-400/15"
                  }`}
                  onClick={() => handleSelect(option.value)}
                >
                  <span>{option.label}</span>
                  {isSelected && <span className="text-xs uppercase tracking-wide">Selected</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default function HomePage() {
  const [error, setError] = useState("");
  const [fallbackMessage, setFallbackMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [booking, setBooking] = useState({
  pickup: null,
  destination: null,
  date: "",
  time: "",
  isASAP: false,
  passengers: "1",
  luggage: "0",
  flightNumber: "",
  phone: "",
});

  const handleChange = (e) => {
    setBooking({
      ...booking,
      [e.target.name]: e.target.value,
    });
  };

  const handleTaxiForNowToggle = () => {
    const nextIsASAP = !booking.isASAP;

    setBooking({
      ...booking,
      isASAP: nextIsASAP,
      date: nextIsASAP ? getTodayDateString() : booking.date,
      time: nextIsASAP ? getCurrentTimeString() : booking.time,
    });
  };

  const getBookingWhatsappLink = (bookingDetails) => {
    const dateText = bookingDetails.isASAP
      ? `${bookingDetails.date} (ASAP pickup / Taxi for now)`
      : bookingDetails.date;
    const timeText = bookingDetails.isASAP
      ? `${bookingDetails.time} (ASAP pickup / Taxi for now)`
      : bookingDetails.time;
    const bookingMessage = encodeURIComponent(
  `Hello, I would like to book a taxi.

Pickup: ${bookingDetails.pickup?.label || ""}
Destination: ${bookingDetails.destination?.label || ""}
Date: ${dateText}
Time: ${timeText}
Passengers: ${bookingDetails.passengers}
Luggage: ${bookingDetails.luggage}
Flight Number: ${bookingDetails.flightNumber || "Not provided"}
Phone: ${bookingDetails.phone}`
    );

    return `https://wa.me/35796613191?text=${bookingMessage}`;
  };

  const handleBookingSubmit = async (e) => {
  e.preventDefault();

  if (isSubmitting) return;

  const bookingForSubmit = booking.isASAP
    ? {
        ...booking,
        date: getTodayDateString(),
        time: getCurrentTimeString(),
        isASAP: true,
      }
    : booking;
  const submitWhatsappLink = getBookingWhatsappLink(bookingForSubmit);
  const validationError = validateBooking(bookingForSubmit);

  if (validationError) {
    setFallbackMessage("");
    setError(validationError);
    return;
  }

  setError("");
  setFallbackMessage("");

  const lastBookingTime = localStorage.getItem("lastBookingTime");
  const now = Date.now();

  if (lastBookingTime && now - Number(lastBookingTime) < 60000) {
    setFallbackMessage("");
    setError("Please wait 60 seconds before sending another booking request.");
    return;
  }

  if (!privacyConsent) {
    setFallbackMessage("");
    setError("Please agree to the Privacy Policy before sending your booking request.");
    return;
  }

  try {
    const rawPickup = sanitizeInput(bookingForSubmit.pickup?.label || bookingForSubmit.pickup);
    const rawDestination = sanitizeInput(
      bookingForSubmit.destination?.label || bookingForSubmit.destination
    );
    const emailPhone = safeTrim(bookingForSubmit.phone);

    const bookingPayload = {
      pickup: rawPickup,
      destination: rawDestination,
      date: sanitizeInput(bookingForSubmit.date),
      time: sanitizeInput(bookingForSubmit.time),
      passengers: bookingForSubmit.passengers,
      luggage: bookingForSubmit.luggage,
      flight_number: sanitizeInput(bookingForSubmit.flightNumber),
      phone: emailPhone,
      isASAP: bookingForSubmit.isASAP,
      status: "new",
    };

    // Public booking submissions go through the submit-booking Edge Function,
    // which performs server-side validation, sanitization, and rate limiting.
    // Remove the old public INSERT RLS policy only after live testing succeeds.
    setIsSubmitting(true);
    const { data: bookingResponse, error: supabaseError } = await supabase.functions.invoke(
      "submit-booking",
      { body: bookingPayload }
    );

    if (supabaseError) {
      console.error("Supabase booking function error:", {
        name: supabaseError.name,
        message: supabaseError.message,
      });

      setFallbackMessage("Your WhatsApp booking message is ready. Please send it to confirm your taxi.");
      setIsSubmitting(false);
      window.open(submitWhatsappLink, "_blank");
      return;
    }

    if (bookingResponse?.warning) {
      console.warn("Booking submitted with notification warning:", bookingResponse.warning);
    }

    localStorage.setItem("lastBookingTime", Date.now().toString());

    setIsSubmitting(false);
    window.open(submitWhatsappLink, "_blank");

    setBooking({
      pickup: null,
      destination: null,
      date: "",
      time: "",
      isASAP: false,
      passengers: "1",
      luggage: "0",
      flightNumber: "",
      phone: "",
    });
    setPrivacyConsent(false);

    setFallbackMessage("");
    setSuccessMessage(true);
    trackBookingSubmit("home_booking_form");

    setTimeout(() => {
      setSuccessMessage(false);
    }, 5000);
  } catch (unexpectedError) {
    console.error("Unexpected booking submission error:", {
      name: unexpectedError instanceof Error ? unexpectedError.name : "UnknownError",
      message: unexpectedError instanceof Error ? unexpectedError.message : "Unknown booking error",
    });
    setError("We could not send your booking right now. Please use WhatsApp to complete your request.");
    setFallbackMessage("Your WhatsApp booking message is ready. Please send it to confirm your taxi.");
    setIsSubmitting(false);
    window.open(submitWhatsappLink, "_blank");
  } finally {
    setIsSubmitting(false);
  }
};
  const galleryImages = [taxi1, taxi2, taxi3];
  const shouldReduceMotion = useReducedMotion();

  const fadeUp = {
    hidden: { opacity: 0, y: shouldReduceMotion ? 0 : 28 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: shouldReduceMotion ? 0 : 0.65, ease: "easeOut" },
    },
  };

  const staggerContainer = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: shouldReduceMotion ? 0 : 0.08,
      },
    },
  };

  const slideRight = {
    hidden: { opacity: 0, x: shouldReduceMotion ? 0 : 36 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: shouldReduceMotion ? 0 : 0.65, ease: "easeOut" },
    },
  };

  const bookingForm = (
    <div id="booking" className="w-full">
      <div className="mb-6 text-center lg:text-left">
        <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
          Fast online request
        </p>

        <h2 className="premium-section-heading mt-3 text-3xl font-black tracking-tight text-white md:text-4xl">
          Book Your Taxi Transfer
        </h2>

        <p className="mt-3 text-base leading-7 text-neutral-300">
          Fast confirmation by phone or WhatsApp.
        </p>
      </div>

      <div className="rounded-[2rem] border border-yellow-400/25 bg-gradient-to-br from-[#111827] via-[#080d1a] to-[#050505] p-4 shadow-2xl shadow-yellow-400/10 ring-1 ring-white/5 md:p-5 lg:p-6">
        <div className="space-y-5">
          <div className="rounded-[1.5rem] border border-white/10 bg-black/20 p-4 md:p-5">
            <div className="mb-4 flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-yellow-400 text-sm font-black text-black">
                1
              </span>
              <div>
                <h3 className="text-lg font-black text-white">
                  Route Details
                </h3>
                <p className="mt-1 text-sm text-neutral-500">
                  Tell us where your ride starts and ends.
                </p>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
              <GooglePlacesAutocomplete
                apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                selectProps={{
                  value: booking.pickup,
                  onChange: (value) =>
                    setBooking({
                      ...booking,
                      pickup: value || null,
                    }),
                  isClearable: true,
                  placeholder: "Pickup location",
                  styles: {
                    control: (base, state) => ({
                      ...base,
                      backgroundColor: "#0b1020",
                      borderColor: state.isFocused ? "#facc15" : "rgba(255,255,255,0.12)",
                      borderRadius: "1rem",
                      boxShadow: state.isFocused ? "0 0 0 3px rgba(250,204,21,0.18)" : "inset 0 2px 10px rgba(0,0,0,0.28)",
                      padding: "0.5rem",
                      minHeight: "60px",
                      transition: "border-color 160ms ease, box-shadow 160ms ease",
                    }),
                    input: (base) => ({
                      ...base,
                      color: "white",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "#a3a3a3",
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "white",
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: "#111827",
                      borderRadius: "1rem",
                      overflow: "hidden",
                      zIndex: 9999,
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isFocused ? "#facc15" : "#111827",
                      color: state.isFocused ? "black" : "white",
                      cursor: "pointer",
                    }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      color: "#a3a3a3",
                    }),
                  },
                }}
              />

              <GooglePlacesAutocomplete
                apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                selectProps={{
                  value: booking.destination,
                  onChange: (value) =>
                    setBooking({
                      ...booking,
                      destination: value || null,
                    }),
                  isClearable: true,
                  placeholder: "Destination",
                  styles: {
                    control: (base, state) => ({
                      ...base,
                      backgroundColor: "#0b1020",
                      borderColor: state.isFocused ? "#facc15" : "rgba(255,255,255,0.12)",
                      borderRadius: "1rem",
                      boxShadow: state.isFocused ? "0 0 0 3px rgba(250,204,21,0.18)" : "inset 0 2px 10px rgba(0,0,0,0.28)",
                      padding: "0.5rem",
                      minHeight: "60px",
                      transition: "border-color 160ms ease, box-shadow 160ms ease",
                    }),
                    input: (base) => ({
                      ...base,
                      color: "white",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "#a3a3a3",
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "white",
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: "#111827",
                      borderRadius: "1rem",
                      overflow: "hidden",
                      zIndex: 9999,
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isFocused ? "#facc15" : "#111827",
                      color: state.isFocused ? "black" : "white",
                      cursor: "pointer",
                    }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      color: "#a3a3a3",
                    }),
                  },
                }}
              />
            </div>
          </div>

          <div className="rounded-[1.5rem] border border-white/10 bg-black/20 p-4 md:p-5">
            <div className="mb-4 flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-yellow-400 text-sm font-black text-black">
                2
              </span>
              <div>
                <h3 className="text-lg font-black text-white">
                  Travel Details
                </h3>
                <p className="mt-1 text-sm text-neutral-500">
                  Add timing, passenger and luggage information.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={handleTaxiForNowToggle}
              className={`premium-input mb-4 flex min-h-[60px] w-full items-center justify-between gap-4 rounded-2xl border px-4 py-4 text-left outline-none shadow-inner shadow-black/30 ring-yellow-400/20 ${
                booking.isASAP
                  ? "border-yellow-400/60 bg-yellow-400 text-black"
                  : "border-white/10 bg-[#0b1020] text-white"
              }`}
              aria-pressed={booking.isASAP}
            >
              <span>
                <span className="block font-extrabold">Taxi for now</span>
                <span
                  className={`mt-1 block text-sm font-semibold ${
                    booking.isASAP ? "text-black/70" : "text-neutral-400"
                  }`}
                >
                  ASAP pickup / Taxi for now
                </span>
              </span>
              <span
                className={`flex h-6 w-11 items-center rounded-full p-1 transition-colors ${
                  booking.isASAP ? "bg-black/20" : "bg-white/10"
                }`}
              >
                <span
                  className={`h-4 w-4 rounded-full transition-transform ${
                    booking.isASAP
                      ? "translate-x-5 bg-black"
                      : "translate-x-0 bg-yellow-400"
                  }`}
                />
              </span>
            </button>

            {booking.isASAP && (
              <div className="mb-4 rounded-2xl border border-yellow-400/30 bg-yellow-400/10 px-4 py-3 text-sm font-bold text-yellow-200">
                ASAP pickup / Taxi for now
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              {!booking.isASAP && (
                <>
                  <input
                    id="booking-date"
                    type="date"
                    name="date"
                    min={getTodayDateString()}
                    value={booking.date}
                    onChange={handleChange}
                    onClick={(e) => e.currentTarget.showPicker?.()}
                    className={`premium-input min-h-[60px] w-full cursor-pointer rounded-2xl border border-white/10 bg-[#0b1020] px-4 py-4 outline-none shadow-inner shadow-black/30 ring-yellow-400/20 accent-yellow-400 ${
                      booking.date ? "text-white" : "text-neutral-400"
                    }`}
                  />

                  <input
                    id="booking-time"
                    type="time"
                    name="time"
                    value={booking.time}
                    onChange={handleChange}
                    onClick={(e) => e.currentTarget.showPicker?.()}
                    className={`premium-input min-h-[60px] w-full cursor-pointer rounded-2xl border border-white/10 bg-[#0b1020] px-4 py-4 outline-none shadow-inner shadow-black/30 ring-yellow-400/20 ${
                      booking.time ? "text-white" : "text-neutral-400"
                    }`}
                  />
                </>
              )}
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <PremiumDropdown
                name="passengers"
                value={booking.passengers}
                onChange={handleChange}
                options={passengerOptions}
              />
              <PremiumDropdown
                name="luggage"
                value={booking.luggage}
                onChange={handleChange}
                options={luggageOptions}
              />
            </div>

            <input
              name="flightNumber"
              value={booking.flightNumber}
              onChange={handleChange}
              className="premium-input mt-4 min-h-[60px] w-full rounded-2xl border border-white/10 bg-[#0b1020] px-4 py-4 outline-none placeholder:text-neutral-400 shadow-inner shadow-black/30 ring-yellow-400/20"
              placeholder="Flight number optional"
            />
          </div>

          <div className="rounded-[1.5rem] border border-white/10 bg-black/20 p-4 md:p-5">
            <div className="mb-4 flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-yellow-400 text-sm font-black text-black">
                3
              </span>
              <div>
                <h3 className="text-lg font-black text-white">
                  Contact Details
                </h3>
                <p className="mt-1 text-sm text-neutral-500">
                  We use this to confirm your ride quickly.
                </p>
              </div>
            </div>

            <PhoneInput
              country="cy"
              enableSearch
              value={booking.phone}
              onChange={(value) =>
                setBooking({
                  ...booking,
                  phone: value ? `+${value}` : "",
                })
              }
              inputProps={{
                name: "phone",
                autoComplete: "tel",
              }}
              containerClass="premium-phone-field"
              inputClass="premium-phone-input"
              buttonClass="premium-phone-button"
              dropdownClass="premium-phone-dropdown"
              searchClass="premium-phone-search"
              placeholder="Your phone number"
            />
          </div>

          {successMessage && (
            <div className="rounded-xl border border-green-500/30 bg-green-500/10 px-4 py-4 text-center font-bold text-green-400">
              Booking request sent successfully.
            </div>
          )}
          {fallbackMessage && (
            <div className="rounded-xl border border-yellow-400/30 bg-yellow-400/10 px-4 py-4 text-center text-sm font-bold text-yellow-300">
              {fallbackMessage}
            </div>
          )}
          {error && (
            <p className="rounded-xl bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-400">
              {error}
            </p>
          )}
          <label className="flex items-start gap-3 rounded-2xl border border-white/10 bg-black/20 p-4 text-sm font-medium leading-6 text-neutral-300">
            <input
              type="checkbox"
              checked={privacyConsent}
              onChange={(e) => setPrivacyConsent(e.target.checked)}
              required
              className="mt-1 h-5 w-5 shrink-0 accent-yellow-400"
            />
            <span>
              I agree to the{" "}
              <Link
                to="/privacy-policy"
                className="font-bold text-yellow-400 underline-offset-4 hover:underline"
              >
                Privacy Policy
              </Link>{" "}
              and understand my details will be used to arrange my taxi booking.
            </span>
          </label>
          <button
            type="button"
            disabled={isSubmitting}
            aria-busy={isSubmitting}
            aria-disabled={isSubmitting}
            onClick={(e) => {
              trackBookingClick("home_booking_form_button");
              handleBookingSubmit(e);
            }}
            className="premium-button flex w-full items-center justify-center gap-3 rounded-2xl bg-yellow-400 px-6 py-5 text-center text-lg font-extrabold text-black shadow-xl shadow-yellow-400/20 hover:bg-yellow-300 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting && (
              <span
                aria-hidden="true"
                className="h-5 w-5 animate-spin rounded-full border-2 border-black/30 border-t-black"
              />
            )}
            {isSubmitting ? "Sending Booking..." : "Book Taxi Now"}
          </button>

          <p className="text-center text-sm font-medium text-neutral-500">
            No payment required online. We confirm your booking before the ride.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <main className="homepage-premium min-h-screen overflow-hidden bg-[#030712] text-white">
      {isSubmitting && (
        <div
          className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/80 px-5 backdrop-blur-md"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="w-full max-w-md rounded-[2rem] border border-yellow-400/30 bg-gradient-to-br from-[#111827] via-[#080d1a] to-black p-7 text-center shadow-2xl shadow-yellow-400/20 sm:p-10">
            <span
              aria-hidden="true"
              className="mx-auto block h-16 w-16 animate-spin rounded-full border-4 border-yellow-400/20 border-t-yellow-400 sm:h-20 sm:w-20"
            />
            <h2 className="mt-7 text-2xl font-black tracking-tight text-white sm:text-3xl">
              Sending Your Booking
            </h2>
            <p className="mt-4 text-base leading-7 text-neutral-300">
              Please wait while we securely submit your taxi request.
            </p>
            <p className="mt-3 text-sm font-semibold text-yellow-300">
              Do not close or refresh this page.
            </p>
            <span className="sr-only">Your booking request is being sent.</span>
          </div>
        </div>
      )}
      <Helmet>
        <title>Limassol Airport Taxi | 24/7 Airport Transfers in Cyprus</title>
        <meta
  name="description"
  content="Book reliable Limassol Airport Taxi transfers to Larnaca Airport, Paphos Airport, Nicosia, Ayia Napa and Protaras. 24/7 airport transfers, fixed prices and WhatsApp booking across Cyprus."
/>
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content="Limassol Airport Taxi | 24/7 Airport Transfers in Cyprus"
        />
        <meta
          property="og:description"
          content="Book reliable Limassol Airport Taxi transfers to Larnaca Airport, Paphos Airport, Nicosia, Ayia Napa and Protaras. 24/7 airport transfers, fixed prices and WhatsApp booking across Cyprus."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://limassolairporttaxi.com/" />
        <meta
          property="og:image"
          content="https://limassolairporttaxi.com/og-image.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Limassol Airport Taxi | 24/7 Airport Transfers in Cyprus"
        />
        <meta
          name="twitter:description"
          content="Book reliable Limassol Airport Taxi transfers to Larnaca Airport, Paphos Airport, Nicosia, Ayia Napa and Protaras. 24/7 airport transfers, fixed prices and WhatsApp booking across Cyprus."
        />
        <meta
          name="twitter:image"
          content="https://limassolairporttaxi.com/og-image.jpg"
        />
        <link rel="canonical" href="https://limassolairporttaxi.com/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TaxiService",
            name: "Limassol Airport Taxi",
            url: "https://limassolairporttaxi.com",
            telephone: "+35796613191",
            areaServed: [
              "Limassol",
              "Larnaca",
              "Paphos",
              "Nicosia",
              "Ayia Napa",
              "Protaras",
              "Troodos",
            ],
            priceRange: "€€",
            serviceType: "Airport Transfers and Taxi Service",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Limassol",
              addressCountry: "CY",
            },
          })}
        </script>
      </Helmet>
      <style>
        {`
          .homepage-premium .premium-section-heading {
            position: relative;
            display: inline-block;
            padding-bottom: 0.75rem;
          }

          .homepage-premium .premium-section-heading::after {
            content: "";
            position: absolute;
            left: 50%;
            bottom: 0;
            width: 4rem;
            height: 0.2rem;
            border-radius: 999px;
            background: linear-gradient(90deg, transparent, #facc15, transparent);
            transform: translateX(-50%) scaleX(0.55);
            transform-origin: center;
            opacity: 0.82;
            transition: transform 360ms ease, opacity 360ms ease, box-shadow 360ms ease;
          }

          .homepage-premium section:hover .premium-section-heading::after {
            opacity: 1;
            transform: translateX(-50%) scaleX(1);
            box-shadow: 0 0 24px rgba(250, 204, 21, 0.28);
          }

          .homepage-premium .premium-card-hover,
          .homepage-premium .premium-route-card,
          .homepage-premium .premium-review-card,
          .homepage-premium .premium-trust-badge {
            will-change: transform;
            backface-visibility: hidden;
          }

          .homepage-premium .premium-card-hover,
          .homepage-premium .premium-route-card {
            transition: transform 280ms ease, border-color 280ms ease, box-shadow 280ms ease, background 280ms ease;
          }

          .homepage-premium .premium-card-hover:hover,
          .homepage-premium .premium-route-card:hover {
            transform: translateY(-9px) scale(1.015);
            box-shadow: 0 24px 70px rgba(0, 0, 0, 0.36), 0 0 34px rgba(250, 204, 21, 0.12);
          }

          .homepage-premium .premium-trust-badge {
            transition: transform 240ms ease, color 240ms ease, border-color 240ms ease, background-color 240ms ease, box-shadow 240ms ease;
          }

          .homepage-premium .premium-trust-badge:hover {
            color: #fef9c3;
            border-color: rgba(250, 204, 21, 0.64);
            background-color: rgba(250, 204, 21, 0.12);
            box-shadow: 0 12px 28px rgba(250, 204, 21, 0.08);
            transform: translateY(-2px);
          }

          .homepage-premium .premium-button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            transition: transform 220ms ease, border-color 220ms ease, background-color 220ms ease, color 220ms ease, box-shadow 220ms ease;
            will-change: transform;
          }

          .homepage-premium .premium-button.block,
          .homepage-premium .premium-button.w-full {
            display: flex;
          }

          .homepage-premium .premium-button:hover {
            transform: translateY(-3px) scale(1.025);
            box-shadow: 0 14px 38px rgba(250, 204, 21, 0.22), 0 0 18px rgba(250, 204, 21, 0.12);
          }

          .homepage-premium .premium-dark-button:hover {
            box-shadow: 0 14px 38px rgba(0, 0, 0, 0.32), 0 0 18px rgba(250, 204, 21, 0.14);
          }

          .homepage-premium .premium-review-card {
            position: relative;
            overflow: hidden;
            transition: transform 280ms ease, border-color 280ms ease, box-shadow 280ms ease, background 280ms ease;
          }

          .homepage-premium .premium-review-card::before {
            content: "";
            position: absolute;
            inset: 0;
            border-radius: inherit;
            border: 1px solid transparent;
            background: linear-gradient(135deg, rgba(250, 204, 21, 0.65), transparent 34%, rgba(250, 204, 21, 0.2)) border-box;
            mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
            mask-composite: exclude;
            opacity: 0;
            pointer-events: none;
            transition: opacity 280ms ease;
          }

          .homepage-premium .premium-review-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 24px 70px rgba(0, 0, 0, 0.34), 0 0 32px rgba(250, 204, 21, 0.1);
          }

          .homepage-premium .premium-review-card:hover::before {
            opacity: 1;
          }

          .homepage-premium .premium-input,
          .homepage-premium .premium-input-shell {
            transition: transform 220ms ease, border-color 220ms ease, box-shadow 220ms ease, background-color 220ms ease;
          }

          .homepage-premium .premium-input:hover,
          .homepage-premium .premium-input-shell:hover {
            border-color: rgba(250, 204, 21, 0.48);
          }

          .homepage-premium .premium-input:focus,
          .homepage-premium .premium-input-shell:focus-within {
            transform: translateY(-1px);
            border-color: #facc15;
            box-shadow: inset 0 2px 10px rgba(0, 0, 0, 0.28), 0 0 0 4px rgba(250, 204, 21, 0.14), 0 16px 36px rgba(0, 0, 0, 0.2);
          }

          .homepage-premium .premium-phone-field.react-tel-input {
            font-family: inherit;
            position: relative;
          }

          .homepage-premium .premium-phone-field .premium-phone-input.form-control {
            min-height: 60px;
            height: 60px;
            width: 100%;
            border-radius: 1rem;
            border: 1px solid rgba(255, 255, 255, 0.1);
            background: #0b1020;
            color: #ffffff;
            padding-left: 66px;
            font-size: 1rem;
            outline: none;
            box-shadow: inset 0 2px 10px rgba(0, 0, 0, 0.3);
            transition: transform 220ms ease, border-color 220ms ease, box-shadow 220ms ease, background-color 220ms ease;
          }

          .homepage-premium .premium-phone-field .premium-phone-input.form-control::placeholder {
            color: rgb(163, 163, 163);
          }

          .homepage-premium .premium-phone-field .premium-phone-input.form-control:hover,
          .homepage-premium .premium-phone-field .premium-phone-input.form-control:focus {
            border-color: #facc15;
            box-shadow: inset 0 2px 10px rgba(0, 0, 0, 0.28), 0 0 0 4px rgba(250, 204, 21, 0.14), 0 16px 36px rgba(0, 0, 0, 0.2);
          }

          .homepage-premium .premium-phone-field .premium-phone-button.flag-dropdown {
            bottom: 1px;
            left: 1px;
            top: 1px;
            width: 56px;
            border-radius: 1rem 0 0 1rem;
            border: 0;
            border-right: 0;
            background: linear-gradient(180deg, rgba(17, 24, 39, 0.96), rgba(5, 8, 22, 0.96));
            box-shadow: inset -1px 0 0 rgba(255, 255, 255, 0.08);
            transition: background-color 180ms ease, box-shadow 180ms ease;
          }

          .homepage-premium .premium-phone-field .premium-phone-button.flag-dropdown.open,
          .homepage-premium .premium-phone-field .premium-phone-button.flag-dropdown:hover {
            background: linear-gradient(180deg, rgba(250, 204, 21, 0.18), rgba(17, 24, 39, 0.96));
            box-shadow: inset -1px 0 0 rgba(250, 204, 21, 0.3), 0 0 0 3px rgba(250, 204, 21, 0.1);
          }

          .homepage-premium .premium-phone-field .selected-flag {
            align-items: center;
            border-radius: 1rem 0 0 1rem;
            display: flex;
            justify-content: center;
            padding: 0 16px 0 14px;
            width: 56px;
          }

          .homepage-premium .premium-phone-field .selected-flag:hover,
          .homepage-premium .premium-phone-field .selected-flag:focus {
            background: transparent;
          }

          .homepage-premium .premium-phone-field .selected-flag .flag {
            transform: scale(1.08);
          }

          .homepage-premium .premium-phone-field .selected-flag .arrow {
            border-top-color: #d4d4d4;
            left: 36px;
            margin-top: -1px;
          }

          .homepage-premium .premium-phone-field .selected-flag .arrow.up {
            border-bottom-color: #facc15;
            border-top-color: transparent;
          }

          .homepage-premium .premium-phone-dropdown.country-list {
            border: 1px solid rgba(250, 204, 21, 0.28);
            border-radius: 1rem;
            background: #080d1a;
            color: #ffffff;
            margin-top: 0.5rem;
            overflow: hidden;
            padding: 0.35rem;
            width: min(360px, calc(100vw - 48px));
            z-index: 50;
            box-shadow: 0 24px 70px rgba(0, 0, 0, 0.58), 0 0 0 1px rgba(255, 255, 255, 0.04);
          }

          .homepage-premium .premium-phone-dropdown .search {
            background: #080d1a;
            padding: 0.55rem;
          }

          .homepage-premium .premium-phone-dropdown .country {
            align-items: center;
            background: transparent;
            border-radius: 0.75rem;
            display: flex;
            gap: 0.45rem;
            line-height: 1.25;
            margin: 0.1rem 0;
            padding: 0.65rem 0.75rem;
            transition: background-color 160ms ease, color 160ms ease;
          }

          .homepage-premium .premium-phone-dropdown .country:hover,
          .homepage-premium .premium-phone-dropdown .country.highlight {
            background: rgba(250, 204, 21, 0.16);
          }

          .homepage-premium .premium-phone-dropdown .country.highlight {
            box-shadow: inset 3px 0 0 #facc15;
          }

          .homepage-premium .premium-phone-dropdown .country-name {
            color: #ffffff;
            flex: 1;
            font-size: 0.95rem;
            font-weight: 700;
            white-space: normal;
          }

          .homepage-premium .premium-phone-dropdown .dial-code {
            color: #facc15;
            font-size: 0.9rem;
            font-weight: 800;
            margin-left: auto;
          }

          .homepage-premium .premium-phone-dropdown .country .flag {
            flex: 0 0 auto;
            margin-right: 0.25rem;
          }

          .homepage-premium .premium-phone-dropdown .premium-phone-search.search-box {
            border: 1px solid rgba(255, 255, 255, 0.14);
            border-radius: 0.75rem;
            background: #050816;
            color: #ffffff;
            font-size: 0.95rem;
            height: 42px;
            margin: 0;
            outline: none;
            padding: 0.65rem 0.8rem;
            width: 100%;
            box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.32);
          }

          .homepage-premium .premium-phone-dropdown .premium-phone-search.search-box::placeholder {
            color: #a3a3a3;
          }

          .homepage-premium .premium-phone-dropdown .premium-phone-search.search-box:focus {
            border-color: #facc15;
            box-shadow: 0 0 0 3px rgba(250, 204, 21, 0.14);
          }

          @media (max-width: 480px) {
            .homepage-premium .premium-phone-field .premium-phone-input.form-control {
              padding-left: 64px;
            }

            .homepage-premium .premium-phone-dropdown.country-list {
              left: 0;
              max-height: 320px;
              width: min(340px, calc(100vw - 32px));
            }

            .homepage-premium .premium-phone-dropdown .country {
              padding: 0.72rem 0.7rem;
            }
          }

          @media (prefers-reduced-motion: reduce) {
            .homepage-premium *,
            .homepage-premium *::before,
            .homepage-premium *::after {
              transition-duration: 1ms !important;
              animation-duration: 1ms !important;
            }

            .homepage-premium .premium-card-hover:hover,
            .homepage-premium .premium-route-card:hover,
            .homepage-premium .premium-review-card:hover,
            .homepage-premium .premium-button:hover,
            .homepage-premium .premium-input:focus,
            .homepage-premium .premium-input-shell:focus-within,
            .homepage-premium .premium-phone-field .premium-phone-input.form-control:hover,
            .homepage-premium .premium-phone-field .premium-phone-input.form-control:focus {
              transform: none;
            }
          }
        `}
      </style>
      {/* HERO */}
      <motion.section
        className="relative flex min-h-screen items-center justify-center overflow-hidden bg-cover bg-center px-6 pb-12 pt-36 md:pb-14 md:pt-40 lg:pb-16 lg:pt-44"
        style={{
          backgroundImage: `url(${heroImage})`,
        }}
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_22%_18%,rgba(250,204,21,0.14),transparent_34%),linear-gradient(115deg,rgba(3,7,18,0.64),rgba(3,7,18,0.42)_48%,rgba(0,0,0,0.56))]" />
        <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-[#030712]/95 to-transparent" />
        <div className="relative z-10 mx-auto grid w-full max-w-7xl items-start gap-8 lg:grid-cols-[minmax(0,0.95fr)_minmax(460px,1.05fr)] lg:gap-12">
          <div className="pt-2 text-center lg:sticky lg:top-20 lg:text-left">
            <motion.p
              variants={fadeUp}
              className="mx-auto mb-5 inline-flex items-center rounded-full border border-yellow-400/30 bg-yellow-400/10 px-5 py-2 text-sm font-bold uppercase tracking-[0.18em] text-yellow-300 shadow-lg shadow-yellow-400/10 lg:mx-0"
            >
              Premium Cyprus Taxi Transfers
            </motion.p>

            <motion.h1
              variants={fadeUp}
              className="text-5xl font-black leading-[0.98] tracking-tight md:text-7xl lg:text-[5.35rem]"
            >
              24/7 Taxi & Airport Transfers
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-neutral-200 md:text-xl lg:mx-0"
            >
              Reliable taxi transfers in Limassol, Larnaca Airport,
              Paphos Airport, Nicosia, Ayia Napa, and across Cyprus.
            </motion.p>

            <motion.div variants={fadeUp} className="mx-auto mt-5 max-w-3xl lg:mx-0">
              <div className="inline-flex items-center gap-3 rounded-full border border-yellow-400/20 bg-black/30 px-4 py-2 text-sm font-bold text-yellow-200 shadow-lg shadow-black/20 backdrop-blur">
                <span className="flex gap-0.5 text-yellow-300">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <FaStar key={star} />
                  ))}
                </span>
                <span>4.9/5 Customer Rating</span>
              </div>

              <motion.div
                variants={staggerContainer}
                className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
              >
                {[
                  ["5000+", "Transfers"],
                  ["24/7", "Availability"],
                  ["100%", "Fixed Prices"],
                  ["Flight", "Monitoring"],
                ].map(([value, label]) => (
                  <motion.div
                    key={`${value}-${label}`}
                    variants={fadeUp}
                    className="premium-card-hover rounded-2xl border border-white/10 bg-black/35 px-4 py-3 text-center shadow-xl shadow-black/20 backdrop-blur hover:border-yellow-400/45"
                  >
                    <p className="text-2xl font-black leading-none text-white md:text-3xl">
                      {value}
                    </p>
                    <p className="mt-2 text-xs font-bold uppercase tracking-[0.16em] text-yellow-300">
                      {label}
                    </p>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            <motion.div variants={fadeUp} className="mt-6 flex flex-col justify-center gap-4 sm:flex-row lg:justify-start">
              <motion.a
                href="tel:+35796613191"
                onClick={() => trackPhoneClick("home_hero")}
                className="premium-button rounded-2xl bg-yellow-400 px-8 py-4 text-center font-extrabold text-black shadow-2xl shadow-yellow-400/20 hover:bg-yellow-300"
                whileHover={shouldReduceMotion ? undefined : { scale: 1.03 }}
                whileTap={shouldReduceMotion ? undefined : { scale: 0.98 }}
              >
                Call Now
              </motion.a>

              <motion.a
                href="#booking"
                onClick={() => trackBookingClick("home_hero")}
                className="premium-button rounded-2xl border border-white/20 bg-white/10 px-8 py-4 text-center font-extrabold text-white backdrop-blur hover:border-yellow-400/50 hover:bg-white/20"
                whileHover={shouldReduceMotion ? undefined : { scale: 1.03 }}
                whileTap={shouldReduceMotion ? undefined : { scale: 0.98 }}
              >
                WhatsApp Booking
              </motion.a>
            </motion.div>
          </div>

          <motion.div variants={slideRight} className="w-full">
            {bookingForm}
          </motion.div>
        </div>
      </motion.section>
      {/* TRUST BADGES */}
<section
  className="relative border-y border-yellow-400/10 bg-[#050505] px-6 py-7"
>
  <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-3 lg:flex-nowrap">
    {[
      "24/7 Available",
      "Fixed Prices",
      "Flight Monitoring",
      "WhatsApp Booking",
      "Airport Transfers",
      "Professional Drivers",
    ].map((badge) => (
      <div
        key={badge}
        className="premium-trust-badge rounded-full border border-yellow-400/15 bg-white/[0.04] px-4 py-2 text-sm font-semibold text-neutral-100 shadow-lg shadow-black/20 sm:px-5"
      >
        <span className="mr-2 text-yellow-300">✓</span>
        {badge}
      </div>
    ))}
  </div>
</section>
      {/* POPULAR TAXI ROUTES */}
<section
  className="relative bg-[#030712] px-6 py-28 md:py-32"
>
  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/30 to-transparent" />
  <div className="mx-auto max-w-7xl">
    <div className="mx-auto mb-16 max-w-3xl text-center">
      <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
        Airport & Cyprus transfers
      </p>

      <h2 className="premium-section-heading mt-4 text-4xl font-black tracking-tight text-white md:text-5xl">
        Popular Taxi Routes
      </h2>

      <p className="mx-auto mt-4 max-w-2xl leading-7 text-neutral-400">
        Book private taxi transfers from Limassol to airports, cities,
        resorts, and mountain villages across Cyprus.
      </p>
    </div>

    <div className="grid gap-7 md:grid-cols-3 lg:gap-8">
      {[
        {
          title: "Limassol → Larnaca Airport",
          description:
            "Direct airport taxi from Limassol to Larnaca Airport for departures, business trips, and holiday flights.",
          price: "From €70",
          to: "/limassol-to-larnaca-airport-taxi",
          icon: FaPlane,
        },
        {
          title: "Limassol → Paphos Airport",
          description:
            "Private transfer from Limassol to Paphos Airport with simple booking and comfortable door-to-door pickup.",
          price: "From €75",
          to: "/limassol-to-paphos-airport-taxi",
          icon: FaPlane,
        },
        {
          title: "Limassol → Nicosia",
          description:
            "Comfortable intercity taxi from Limassol to Nicosia for meetings, appointments, and capital city visits.",
          price: "From €90",
          to: "/limassol-to-nicosia",
          icon: FaCity,
        },
        {
          title: "Limassol → Ayia Napa",
          description:
            "Long-distance taxi from Limassol to Ayia Napa for hotels, beaches, nightlife, and resort transfers.",
          price: "From €120",
          to: "/limassol-to-ayia-napa",
          icon: FaWater,
        },
        {
          title: "Limassol → Protaras",
          description:
            "Private taxi transfer from Limassol to Protaras, Fig Tree Bay, villas, hotels, and family resorts.",
          price: "From €130",
          to: "/limassol-to-protaras",
          icon: FaUmbrellaBeach,
        },
        {
          title: "Limassol → Troodos",
          description:
            "Scenic taxi transfer from Limassol to Troodos villages, mountain hotels, wineries, and day-trip stops.",
          price: "From €80",
          to: "/limassol-to-troodos",
          icon: FaMountain,
        },
      ].map((route) => {
        const Icon = route.icon;

        return (
          <article
            key={route.title}
            className="premium-route-card group relative flex h-full flex-col overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/[0.1] via-white/[0.045] to-yellow-400/[0.035] p-7 shadow-2xl shadow-black/30 hover:border-yellow-400/60"
          >
          <div className="absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/50 to-transparent opacity-60" />
          <div className="mb-6 flex items-center justify-between gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-yellow-400/25 bg-yellow-400/10 text-yellow-300 shadow-lg shadow-yellow-400/10 transition duration-200 group-hover:bg-yellow-400 group-hover:text-black">
              <Icon className="text-xl" />
            </div>
            <span className="rounded-full border border-yellow-300/50 bg-yellow-400 px-4 py-2 text-sm font-black text-black shadow-lg shadow-yellow-400/20">
              {route.price}
            </span>
          </div>

          <h3 className="text-2xl font-black leading-tight text-white">
            {route.title}
          </h3>

          <p className="mt-4 flex-1 leading-7 text-neutral-300">
            {route.description}
          </p>

          <div className="mt-7">
          <Link
            to={route.to}
            onClick={() => trackBookingClick("home_route_card")}
            className="premium-button block rounded-2xl bg-yellow-400 px-5 py-3 text-center font-extrabold text-black hover:bg-yellow-300"
          >
            Book Now
          </Link>
          </div>
        </article>
        );
      })}
    </div>
  </div>
</section>
      {/* HOW IT WORKS */}
<section
  className="relative bg-[#09090b] px-6 py-28 md:py-32"
>
  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/25 to-transparent" />
  <div className="mx-auto max-w-7xl">
    <div className="mx-auto mb-16 max-w-3xl text-center">
      <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
        Simple booking process
      </p>

      <h2 className="premium-section-heading mt-4 text-4xl font-black tracking-tight text-white md:text-5xl">
        How It Works
      </h2>

      <p className="mx-auto mt-4 max-w-2xl text-lg leading-8 text-neutral-400">
        Book your airport transfer in three simple steps.
      </p>
    </div>

    <div className="grid gap-7 md:grid-cols-3 lg:gap-8">
      {[
        {
          step: "Step 1",
          title: "Book Online",
          description: "Choose your pickup, destination and travel details.",
          icon: FaCalendarCheck,
        },
        {
          step: "Step 2",
          title: "Driver Confirmation",
          description:
            "We confirm your booking and monitor your flight if needed.",
          icon: FaPhoneAlt,
        },
        {
          step: "Step 3",
          title: "Enjoy Your Ride",
          description:
            "Your professional driver arrives on time and takes you safely to your destination.",
          icon: FaCar,
        },
      ].map((item) => {
        const Icon = item.icon;

        return (
          <article
            key={item.title}
            className="premium-card-hover group relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/[0.09] to-white/[0.025] p-8 shadow-xl shadow-black/30 hover:border-yellow-400/50 md:p-9"
          >
            <div className="absolute right-6 top-6 text-6xl font-bold text-white/[0.03] transition-colors duration-200 group-hover:text-yellow-400/10">
              {item.step.replace("Step ", "0")}
            </div>

            <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-yellow-400 text-black shadow-lg shadow-yellow-400/20">
              <Icon className="text-2xl" />
            </div>

            <p className="relative mt-7 text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
              {item.step}
            </p>

            <h3 className="relative mt-3 text-2xl font-bold text-white">
              {item.title}
            </h3>

            <p className="relative mt-4 leading-7 text-neutral-400">
              {item.description}
            </p>
          </article>
        );
      })}
    </div>

    <div className="mt-12 flex flex-col justify-center gap-4 sm:flex-row">
      <a
        href="#booking"
        onClick={() => trackBookingClick("home_services")}
        className="premium-button rounded-2xl bg-yellow-400 px-8 py-4 text-center font-extrabold text-black shadow-lg shadow-yellow-400/20 hover:bg-yellow-300"
      >
        Book Now
      </a>

      <a
        href="https://wa.me/35796613191?text=Hello,%20I%20would%20like%20to%20book%20a%20taxi."
        onClick={() => trackWhatsAppClick("home_services")}
        target="_blank"
        rel="noreferrer"
        className="premium-button rounded-2xl border border-white/10 bg-white/[0.04] px-8 py-4 text-center font-extrabold text-white hover:border-yellow-400/50 hover:text-yellow-300"
      >
        WhatsApp Booking
      </a>
    </div>
  </div>
</section>
      {/* QUICK CTA */}
<section className="relative bg-[#030712] px-6 py-20 md:py-24">
  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent" />
  <div className="mx-auto max-w-7xl rounded-[2rem] border border-yellow-400/20 bg-gradient-to-br from-yellow-400/12 to-white/[0.035] p-8 text-center shadow-2xl shadow-black/30 md:p-12">
    <h2 className="premium-section-heading text-3xl font-bold text-yellow-300 md:text-5xl">
      Need a Taxi Right Now?
    </h2>

    <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-neutral-300">
      Available 24/7 for Airport Transfers, City Rides and Long Distance Trips.
    </p>

    <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
      <a
        href="tel:+35796613191"
        onClick={() => trackPhoneClick("home_quick_cta")}
        className="premium-button rounded-2xl bg-yellow-400 px-8 py-4 font-bold text-black hover:bg-yellow-300"
      >
        Call Now
      </a>

      <a
        href="https://wa.me/35796613191?text=Hello,%20I%20would%20like%20to%20book%20a%20taxi."
        onClick={() => trackWhatsAppClick("home_quick_cta")}
        target="_blank"
        rel="noreferrer"
        className="premium-button rounded-2xl border border-white/20 bg-white/10 px-8 py-4 font-bold text-white hover:bg-white/20"
      >
        WhatsApp Booking
      </a>
    </div>
  </div>
</section>
      {/* ABOUT */}
<section className="relative bg-[#030712] px-6 py-24 md:py-28">
  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent" />
  <div className="mx-auto grid max-w-7xl items-start gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:gap-14">
    <div className="rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/[0.07] to-white/[0.025] p-7 shadow-2xl shadow-black/25 md:p-9">
      <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
        About Limassol Airport Taxi
      </p>

      <h2 className="premium-section-heading mt-4 text-3xl font-black tracking-tight md:text-4xl">
        Reliable taxi transfers in Limassol and across Cyprus
      </h2>

      <div className="mt-6 space-y-4 border-l border-yellow-400/25 pl-5">
      <p className="leading-7 text-neutral-400">
        Limassol Airport Taxi provides private taxi transfers for tourists,
        business travellers, families, and local customers. We specialise in
        airport transfers from Limassol to Larnaca Airport and Paphos Airport,
        as well as hotel pickups, port transfers, and long-distance trips
        around Cyprus.
      </p>

      <p className="leading-7 text-neutral-400">
        Our goal is simple: punctual pickup, clean vehicles, clear prices,
        and fast booking by phone or WhatsApp.
      </p>
      </div>
    </div>

    <div>
      <div className="grid gap-4 sm:grid-cols-2">
        {[
          { label: "Airport Transfers", icon: FaPlane },
          { label: "Fixed Prices", icon: FaTags },
          { label: "Flight Monitoring", icon: FaRoute },
          { label: "24/7 Availability", icon: FaClock },
        ].map((feature) => {
          const Icon = feature.icon;

          return (
            <div
              key={feature.label}
              className="premium-card-hover rounded-[1.5rem] border border-white/10 bg-white/[0.045] p-5 shadow-xl shadow-black/20 hover:border-yellow-400/45"
            >
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-yellow-400/10 text-yellow-300">
                <Icon />
              </div>
              <p className="font-bold text-white">
                {feature.label}
              </p>
            </div>
          );
        })}
      </div>

    <div className="mt-5 grid gap-4 sm:grid-cols-2">
      {[
        "Limassol",
        "Larnaca Airport",
        "Paphos Airport",
        "Nicosia",
        "Ayia Napa",
        "Protaras",
      ].map((area) => (
        <div
          key={area}
          className="premium-trust-badge rounded-2xl border border-yellow-400/15 bg-white/[0.04] p-7 text-center font-bold text-yellow-300 shadow-lg shadow-black/20"
        >
          {area}
        </div>
      ))}
    </div>
    </div>
  </div>
</section>

      {/* SERVICES */}
      <section className="relative bg-[#09090b] px-6 py-28 md:py-32">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent" />
        <div className="mx-auto max-w-7xl">
        <div className="mx-auto mb-16 max-w-3xl text-center">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
            Professional taxi services
          </p>

          <h2 className="premium-section-heading mt-4 text-4xl font-black tracking-tight md:text-5xl">
            Our Services
          </h2>
        </div>

        <div className="grid gap-7 md:grid-cols-3 lg:gap-8">
          <div className="premium-card-hover rounded-3xl border border-white/10 bg-white/[0.04] p-8 md:p-9">
            <h3 className="text-2xl font-bold text-yellow-300">
              Airport Transfers
            </h3>

            <p className="mt-4 text-neutral-400">
              Transfers from Limassol to Larnaca and Paphos airports.
            </p>
          </div>

          <div className="premium-card-hover rounded-3xl border border-white/10 bg-white/[0.04] p-8 md:p-9">
            <h3 className="text-2xl font-bold text-yellow-300">
              City Taxi
            </h3>

            <p className="mt-4 text-neutral-400">
              Fast and comfortable taxi rides around Limassol.
            </p>
          </div>

          <div className="premium-card-hover rounded-3xl border border-white/10 bg-white/[0.04] p-8 md:p-9">
            <h3 className="text-2xl font-bold text-yellow-300">
              Cyprus Trips
            </h3>

            <p className="mt-4 text-neutral-400">
              Private transfers across Cyprus including Nicosia,
              Ayia Napa, and Protaras.
            </p>
          </div>
        </div>
        </div>
      </section>
{/* POPULAR ROUTES */}
<section className="relative bg-[#030712] px-6 py-28 md:py-32">
  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent" />
  <div className="mx-auto max-w-7xl">
    <div className="mx-auto mb-16 max-w-3xl text-center">
      <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
        Airport & intercity transfers
      </p>

      <h2 className="premium-section-heading mt-4 text-4xl font-black tracking-tight md:text-5xl">
        Popular Routes
      </h2>

      <p className="mx-auto mt-4 max-w-2xl text-neutral-400">
        Our most frequently booked airport and intercity transfers.
      </p>
    </div>

    <div className="grid gap-7 sm:grid-cols-2 lg:grid-cols-4">
      {[
        {
          title: "Larnaca Airport → Limassol",
          price: "From €70",
          description: "Fixed price airport taxi with flight monitoring.",
          button: "View Route",
          to: "/larnaca-airport-to-limassol",
          icon: FaPlaneDeparture,
        },
        {
          title: "Paphos Airport → Limassol",
          price: "From €75",
          description: "Private airport pickup with WhatsApp booking.",
          button: "View Route",
          to: "/paphos-airport-to-limassol",
          icon: FaPlaneDeparture,
        },
        {
          title: "Limassol → Larnaca Airport",
          price: "From €70",
          description: "Private airport transfer available 24/7.",
          button: "View Route",
          to: "/larnaca-airport-taxi",
          icon: FaPlaneDeparture,
        },
        {
          title: "Limassol → Paphos Airport",
          price: "From €75",
          description: "Reliable airport taxi service with fixed pricing.",
          button: "View Route",
          to: "/paphos-airport-taxi",
          icon: FaPlaneDeparture,
        },
        {
          title: "Limassol → Nicosia",
          price: "From €90",
          description: "Comfortable private transfer to the capital.",
          button: "View Route",
          to: "/limassol-to-nicosia",
          icon: FaCity,
        },
        {
          title: "Limassol → Ayia Napa",
          price: "From €120",
          description: "Fast and comfortable long-distance taxi service.",
          button: "View Route",
          to: "/limassol-to-ayia-napa",
          icon: FaUmbrellaBeach,
        },
        {
          title: "Limassol → Protaras",
          price: "From €130",
          description: "Private transfers with hotel pickup available.",
          button: "View Route",
          to: "/limassol-to-protaras",
          icon: FaUmbrellaBeach,
        },
        {
          title: "Limassol → Troodos",
          price: "From €80",
          description: "Comfortable mountain village taxi transfers.",
          button: "View Route",
          to: "/limassol-to-troodos",
          icon: FaMountain,
        },
      ].map((route) => {
        const Icon = route.icon;

        return (
        <div
          key={route.title}
          className="premium-route-card group flex h-full flex-col rounded-[1.75rem] border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.025] p-6 shadow-xl shadow-black/25 hover:border-yellow-400/50"
        >
          <div className="mb-5 flex items-center justify-between gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-yellow-400/20 bg-yellow-400/10 text-yellow-300 transition duration-200 group-hover:bg-yellow-400 group-hover:text-black">
              <Icon />
            </div>
            <span className="rounded-full border border-yellow-400/40 bg-yellow-400/15 px-3 py-1 text-sm font-black text-yellow-200">
              {route.price}
            </span>
          </div>

          <h3 className="text-xl font-black leading-tight text-white">
            {route.title}
          </h3>

          <p className="mt-4 flex-1 leading-7 text-neutral-400">
            {route.description}
          </p>

          {route.to ? (
            <Link
              to={route.to}
              className="premium-button mt-8 rounded-2xl bg-yellow-400 px-5 py-3 text-center font-bold text-black hover:bg-yellow-300"
            >
              {route.button}
            </Link>
          ) : (
            <a
              href={route.href}
              className="premium-button mt-8 rounded-2xl bg-yellow-400 px-5 py-3 text-center font-bold text-black hover:bg-yellow-300"
            >
              {route.button}
            </a>
          )}
        </div>
        );
      })}
    </div>
  </div>
</section>
{/* PRICES */}
<section className="relative bg-[#09090b] px-6 py-28 md:py-32">
  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent" />
  <div className="mx-auto max-w-7xl">
    <div className="mx-auto mb-16 max-w-3xl text-center">
      <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
        Transparent guide pricing
      </p>

      <h2 className="premium-section-heading mt-4 text-4xl font-black tracking-tight md:text-5xl">
        Popular Taxi Prices
      </h2>

      <p className="mt-4 text-neutral-400">
        Prices may change depending on time, luggage, passengers, and public holidays.
      </p>
    </div>

    <div className="grid gap-7 md:grid-cols-4">
      {[
        ["Limassol → Larnaca Airport", "From €70"],
        ["Limassol → Paphos Airport", "From €75"],
        ["Limassol City Taxi", "From €15"],
        ["Limassol → Nicosia", "From €90"],
      ].map(([route, price]) => (
        <div
          key={route}
          className="premium-card-hover rounded-3xl border border-white/10 bg-white/[0.04] p-7 text-center"
        >
          <p className="text-neutral-300">{route}</p>
          <p className="mt-4 text-3xl font-bold text-yellow-300">
            {price}
          </p>
        </div>
      ))}
    </div>
  </div>
</section>
{/* WHY CHOOSE US */}
<section
  className="relative bg-[#030712] px-6 py-28 md:py-32"
>
  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent" />
  <div className="mx-auto grid max-w-7xl items-center gap-14 md:grid-cols-2">
    <div>
      <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
        Why Choose Limassol Airport Taxi
      </p>

      <h2 className="premium-section-heading mt-4 text-4xl font-black tracking-tight md:text-5xl">
        Reliable taxi service for locals and tourists
      </h2>

      <p className="mt-6 text-neutral-400">
        We provide clean vehicles, professional drivers, clear communication,
        and reliable airport transfers across Cyprus.
      </p>
    </div>

    <div className="grid gap-5 sm:grid-cols-2">
      {[
        "24/7 availability",
        "Airport transfers",
        "English-speaking drivers",
        "Fixed prices",
        "Clean cars",
        "Fast WhatsApp booking",
      ].map((item) => (
        <div
          key={item}
          className="premium-trust-badge rounded-2xl border border-white/10 bg-white/[0.04] p-6 font-semibold"
        >
          ✓ {item}
        </div>
      ))}
    </div>
  </div>
</section>
{/* SOCIAL PROOF */}
<section
  className="relative bg-[#09090b] px-6 py-28 md:py-32"
>
  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent" />
  <div className="mx-auto max-w-7xl">
    <div className="mx-auto mb-16 max-w-3xl text-center">
      <div className="mx-auto mb-6 max-w-xl rounded-[2rem] border border-yellow-400/20 bg-gradient-to-br from-white/[0.08] to-white/[0.025] p-8 shadow-2xl shadow-black/30">
        <div className="flex justify-center gap-1 text-3xl text-yellow-300">
          {[1, 2, 3, 4, 5].map((star) => (
            <FaStar key={star} />
          ))}
        </div>

        <h2 className="premium-section-heading mt-4 text-3xl font-bold text-yellow-300 md:text-4xl">
          4.9/5 Customer Rating
        </h2>

        <p className="mt-4 leading-7 text-neutral-400">
          Based on airport transfers, city rides, and private Cyprus taxi trips.
        </p>
      </div>

      <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
        Trusted by Travelers Across Cyprus
      </p>

      <h2 className="premium-section-heading mt-4 text-4xl font-black tracking-tight md:text-5xl">
        Airport transfers with reliable service
      </h2>

      <p className="mx-auto mt-4 max-w-2xl text-neutral-400">
        Airport transfers, hotel pickups, and long-distance taxi trips with fast WhatsApp booking.
      </p>
    </div>

    <div
      className="mx-auto mb-8 grid max-w-4xl gap-3 sm:grid-cols-3"
    >
      {[
        {
          value: "★★★★★",
          label: "4.9/5 Rating",
        },
        {
          value: "5000+",
          label: "Transfers",
        },
        {
          value: "24/7",
          label: "Service",
        },
      ].map((item) => (
        <div
          key={item.label}
          className="premium-trust-badge rounded-2xl border border-yellow-400/15 bg-black/25 px-5 py-4 text-center shadow-lg shadow-black/20"
        >
          <p className="font-black text-yellow-300">
            {item.value}
          </p>
          <p className="mt-1 text-sm font-bold text-white">
            {item.label}
          </p>
        </div>
      ))}
    </div>

    <div className="grid gap-7 md:grid-cols-3 lg:gap-8">
      {[
        {
          name: "Sarah M.",
          country: "United Kingdom",
          route: "Larnaca Airport → Limassol",
          text: "Driver arrived early, helped with luggage, and the car was very clean.",
        },
        {
          name: "Andreas K.",
          country: "Cyprus",
          route: "Limassol City Ride",
          text: "Fast WhatsApp booking, clear price, and professional service.",
        },
        {
          name: "Elena P.",
          country: "Germany",
          route: "Limassol → Paphos Airport",
          text: "Reliable airport transfer with comfortable car and on-time pickup.",
        },
      ].map((review) => (
        <div
          key={review.name}
          className="premium-review-card flex h-full flex-col rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.025] p-8 shadow-xl shadow-black/25 hover:border-yellow-400/40 md:p-9"
        >
          <div className="flex gap-1 text-xl text-yellow-300">
            {[1, 2, 3, 4, 5].map((star) => (
              <FaStar key={star} />
            ))}
          </div>

          <p className="mt-5 text-sm font-semibold uppercase tracking-[0.18em] text-yellow-300">
            {review.route}
          </p>

          <p className="mt-5 flex-1 text-lg leading-8 text-neutral-300">
            “{review.text}”
          </p>

          <div className="mt-8 border-t border-white/10 pt-5">
            <p className="font-bold text-white">
              {review.name}
            </p>
            <p className="mt-1 text-sm text-neutral-500">
              {review.country}
            </p>
          </div>
        </div>
      ))}
    </div>

    <div className="mt-12 overflow-hidden rounded-[2rem] border border-yellow-400/25 bg-gradient-to-br from-yellow-400/20 via-white/[0.055] to-black/30 p-8 text-center shadow-2xl shadow-yellow-400/10 md:p-12">
      <h3 className="mx-auto max-w-3xl text-3xl font-black tracking-tight text-white md:text-5xl">
        Ready to Book Your Airport Transfer?
      </h3>

      <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-neutral-300">
        Fast confirmation by phone or WhatsApp. Fixed prices and professional drivers across Cyprus.
      </p>

      <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
        <a
          href="#booking"
          onClick={() => trackBookingClick("home_airport_transfer_cta")}
          className="premium-button rounded-2xl bg-yellow-400 px-8 py-4 text-center font-extrabold text-black shadow-xl shadow-yellow-400/20 hover:bg-yellow-300"
        >
          Book Taxi Now
        </a>

        <a
          href="https://wa.me/35796613191?text=Hello,%20I%20would%20like%20to%20book%20a%20taxi."
          onClick={() => trackWhatsAppClick("home_airport_transfer_cta")}
          target="_blank"
          rel="noreferrer"
          className="premium-button rounded-2xl border border-yellow-400/30 bg-black/25 px-8 py-4 text-center font-extrabold text-white hover:border-yellow-400/60 hover:text-yellow-300"
        >
          WhatsApp Booking
        </a>
      </div>
    </div>
  </div>
</section>
{/* REVIEWS SLIDER */}
<section className="relative bg-[#030712] px-6 py-28 md:py-32">
  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent" />
  <div className="mx-auto max-w-7xl">
    <div className="mx-auto mb-16 max-w-3xl text-center">
      <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
        Customer Reviews
      </p>

      <h2 className="premium-section-heading mt-4 text-4xl font-black tracking-tight md:text-5xl">
        What tourists say about us
      </h2>

      <p className="mx-auto mt-4 max-w-2xl text-neutral-400">
        Real feedback from customers who booked airport transfers and taxi rides in Limassol.
      </p>
    </div>

    <Swiper
      modules={[Autoplay, Pagination]}
      spaceBetween={24}
      slidesPerView={1}
      loop={true}
      autoplay={{
        delay: 3500,
        disableOnInteraction: false,
      }}
      pagination={{
        clickable: true,
      }}
      breakpoints={{
        768: {
          slidesPerView: 2,
        },
        1024: {
          slidesPerView: 3,
        },
      }}
    >
      {[
        {
          name: "James",
          country: "United Kingdom",
          review:
            "Excellent airport transfer service. The driver arrived on time and the car was very clean.",
        },
        {
          name: "Maria",
          country: "Greece",
          review:
            "Very professional taxi company in Limassol. Fast WhatsApp communication and fair pricing.",
        },
        {
          name: "Daniel",
          country: "Germany",
          review:
            "Reliable transfer from Larnaca Airport to Limassol. I will definitely use them again.",
        },
        {
          name: "Anna",
          country: "Poland",
          review:
            "Easy booking, polite driver, and comfortable ride from the airport to our hotel.",
        },
        {
          name: "Sergey",
          country: "Cyprus",
          review:
            "Good service for local taxi rides in Limassol. Quick response and clean vehicle.",
        },
      ].map((item) => (
        <SwiperSlide key={item.name}>
          <div className="premium-review-card h-full rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.025] p-8 shadow-xl shadow-black/25 hover:border-yellow-400/40">
            <div className="mb-4 flex gap-1 text-xl text-yellow-300">
              {[1, 2, 3, 4, 5].map((star) => (
                <FaStar key={star} />
              ))}
            </div>

            <p className="text-neutral-300">
              “{item.review}”
            </p>

            <div className="mt-6">
              <p className="font-bold text-white">
                {item.name}
              </p>
              <p className="text-sm text-neutral-500">
                {item.country}
              </p>
            </div>
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  </div>
</section>
{/* BOOKING CTA */}
<section className="relative bg-[#09090b] px-6 py-28 md:py-32">
  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent" />
  <div className="mx-auto max-w-7xl rounded-[2rem] border border-yellow-300/30 bg-gradient-to-br from-yellow-300 to-yellow-500 p-10 text-center text-black shadow-2xl shadow-yellow-400/10 md:p-14">
    <h2 className="premium-section-heading text-4xl font-bold">
      Book Your Taxi in Limassol Today
    </h2>

    <p className="mx-auto mt-5 max-w-2xl text-lg">
      Need a reliable taxi to Larnaca Airport, Paphos Airport, your hotel,
      or anywhere in Cyprus? Contact Limassol Airport Taxi now for fast booking.
    </p>

    <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
      <a
        href="tel:+35796613191"
        onClick={() => trackPhoneClick("home_contact_cta")}
        className="premium-button premium-dark-button rounded-2xl bg-black px-8 py-4 font-bold text-white hover:bg-neutral-800"
      >
        Call Now
      </a>

      <a
        href="https://wa.me/35796613191?text=Hello,%20I%20would%20like%20to%20book%20a%20taxi."
        onClick={() => trackWhatsAppClick("home_contact_cta")}
        target="_blank"
        rel="noreferrer"
        className="premium-button premium-dark-button rounded-2xl border border-black px-8 py-4 font-bold text-black hover:bg-black hover:text-white"
      >
        Book on WhatsApp
      </a>
    </div>
  </div>
</section>
{/* REAL TAXI IMAGES */}
<section className="relative bg-[#030712] px-6 py-28 md:py-32">
  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent" />
  <div className="mx-auto max-w-7xl">
  <div className="mx-auto mb-16 max-w-3xl text-center">
    <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
      Our Taxi Service
    </p>

    <h2 className="premium-section-heading mt-4 text-4xl font-black tracking-tight md:text-5xl">
      Clean, Comfortable & Reliable
    </h2>

    <p className="mt-4 text-neutral-400">
      Travel across Limassol and Cyprus with professional drivers and comfortable vehicles.
    </p>
  </div>

  <div className="grid gap-6 md:grid-cols-3">
    {galleryImages.map((image, index) => (
      <div
        key={index}
        className="premium-card-hover overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.04] shadow-xl shadow-black/25 hover:border-yellow-400/40"
      >
        <img
          src={image}
          alt={`Limassol Airport Taxi vehicle ${index + 1}`}
          loading="lazy"
          decoding="async"
          className="h-80 w-full object-cover"
        />
      </div>
    ))}
  </div>
  </div>
</section>
{/* AREAS WE SERVE */}
<section
  className="relative bg-[#09090b] px-6 py-28 md:py-32"
>
  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent" />
  <div className="mx-auto max-w-7xl">
    <div className="mx-auto mb-16 max-w-3xl text-center">
      <p className="text-sm font-bold uppercase tracking-[0.18em] text-yellow-300">
        Cyprus coverage
      </p>

      <h2 className="premium-section-heading mt-4 text-4xl font-black tracking-tight text-white md:text-5xl">
        Areas We Serve
      </h2>

      <p className="mx-auto mt-4 max-w-2xl text-neutral-400">
        Reliable taxi and airport transfer service across Limassol and Cyprus.
      </p>
    </div>

    <div className="grid gap-7 md:grid-cols-2 lg:grid-cols-4">
      {[
        {
          name: "Limassol",
          description: "City rides, hotel pickups, and local transfers.",
        },
        {
          name: "Larnaca Airport",
          description: "Private transfers from Limassol to Larnaca Airport.",
        },
        {
          name: "Paphos Airport",
          description: "Comfortable airport transfers to and from Paphos Airport.",
        },
        {
          name: "Nicosia",
          description: "Reliable intercity taxi service between Limassol and Nicosia.",
        },
        {
          name: "Ayia Napa",
          description: "Direct taxi transfers for hotels, beaches, and nightlife.",
        },
        {
          name: "Protaras",
          description: "Private rides to resorts, villas, and coastal destinations.",
        },
        {
          name: "Troodos",
          description: "Scenic mountain transfers for day trips and hotel stays.",
        },
        {
          name: "Limassol Marina",
          description: "Premium pickup and drop-off service for the marina area.",
        },
      ].map((area) => (
        <div
          key={area.name}
          className="premium-card-hover rounded-[1.75rem] border border-white/10 bg-gradient-to-br from-white/[0.08] to-white/[0.025] p-7 shadow-xl shadow-black/20 hover:border-yellow-400/40"
        >
          <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-2xl bg-yellow-400/10 text-yellow-400">
            <FaMapMarkerAlt />
          </div>

          <h3 className="text-xl font-bold text-yellow-400">
            {area.name}
          </h3>

          <p className="mt-3 leading-7 text-neutral-400">
            {area.description}
          </p>
        </div>
      ))}
    </div>

    <div className="mt-12 rounded-[2rem] border border-yellow-400/20 bg-white/[0.05] p-8 text-center shadow-xl shadow-black/25">
      <h3 className="text-2xl font-bold text-yellow-400">
        Need a taxi in one of these areas?
      </h3>

      <p className="mx-auto mt-3 max-w-2xl text-neutral-400">
        Call or message us on WhatsApp for fast booking.
      </p>

      <div className="mt-6 flex flex-col justify-center gap-4 sm:flex-row">
        <a
          href="tel:+35796613191"
          onClick={() => trackPhoneClick("home_footer_cta")}
          className="premium-button rounded-2xl bg-yellow-400 px-6 py-4 text-center font-bold text-black hover:bg-yellow-300"
        >
          Call Now
        </a>

        <a
          href="https://wa.me/35796613191?text=Hello,%20I%20would%20like%20to%20book%20a%20taxi."
          onClick={() => trackWhatsAppClick("home_footer_cta")}
          target="_blank"
          rel="noreferrer"
          className="premium-button rounded-2xl border border-white/10 bg-white/[0.04] px-6 py-4 text-center font-bold text-white hover:border-yellow-400/50 hover:text-yellow-300"
        >
          WhatsApp Booking
        </a>
      </div>
    </div>
  </div>
</section>
    </main>
  );
}
